import { TaskStatus, toDateOnly } from './taskWorkflow';
import { isLegalCaseTask } from './permission';

export function nestSubtasks(flat) {
  const list = Array.isArray(flat) ? flat : [];
  const parentIdOf = (s) => s.parent_subtask_id || s.parentSubtaskId || null;
  const byId = new Map(list.map((s) => [s.id, s]));
  const children = new Map();
  for (const s of list) {
    const pid = parentIdOf(s);
    if (!children.has(pid)) children.set(pid, []);
    children.get(pid).push(s);
  }
  const walk = (parentId, depth) => {
    return (children.get(parentId) || []).flatMap((s) => {
      const parent = parentId ? byId.get(parentId) : null;
      return [{ ...s, parent_subtask_id: parentIdOf(s), _depth: depth, _parent: parent || null }, ...walk(s.id, depth + 1)];
    });
  };
  return walk(null, 1);
}

export function subtaskDepth(sub, flat) {
  const parentIdOf = (s) => s?.parent_subtask_id || s?.parentSubtaskId || null;
  if (!parentIdOf(sub)) return 1;
  const byId = new Map((flat || []).map((s) => [s.id, s]));
  let depth = 1;
  let cur = sub;
  const seen = new Set();
  while (parentIdOf(cur)) {
    if (seen.has(cur.id)) break;
    seen.add(cur.id);
    depth += 1;
    cur = byId.get(parentIdOf(cur));
    if (!cur) break;
  }
  return depth;
}

export function canAddChild(sub, flat) {
  return subtaskDepth(sub, flat) === 1;
}

export function reviewerId(task, subtask) {
  if (subtask?.approver_id) return subtask.approver_id;
  return task?.owner_id;
}

export function parentDueDate(task, subtask, flat) {
  // A court case's actions are not capped by its target date (the backend
  // passes no root due date for one).
  const rootDue = isLegalCaseTask(task) ? null : task?.dueDate;
  const parentId = subtask?.parent_subtask_id || subtask?.parentSubtaskId;
  if (!parentId) return rootDue;
  const parent = (flat || []).find((s) => s.id === parentId);
  return parent?.plannedEndDate || rootDue;
}

export function childDueAfterParent(childEnd, parentDue) {
  const child = toDateOnly(childEnd);
  const parent = toDateOnly(parentDue);
  if (!child || !parent) return false;
  return child > parent;
}

/** Inclusive end date (YYYY-MM-DD): a 1-day subtask ends on its start date. */
export function plannedEndFromDuration(startDate, duration) {
  const start = toDateOnly(startDate);
  const days = Math.floor(Number(duration));
  if (!start || !Number.isFinite(days) || days < 1) return '';
  const [y, m, d] = start.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days - 1)).toISOString().split('T')[0];
}

export function canStartStatus(status) {
  return status === 'Planned';
}

export function canSubmitStatus(status) {
  return (
    status === 'In Progress' ||
    status === 'Waiting on External' ||
    status === 'Blocked'
  );
}

export function isClosedStatus(status) {
  return status === 'Completed' || status === 'Cancelled';
}

export function isWaived(subtask) {
  return !!(subtask?.waived && String(subtask.waiveReason || '').trim());
}

export function isSubtaskClosed(subtask) {
  return isWaived(subtask) || isClosedStatus(subtask?.status);
}

export function allSubtasksClosed(subtasks) {
  const rows = Array.isArray(subtasks) ? subtasks : [];
  return rows.length > 0 && rows.every(isSubtaskClosed);
}

export function isOpenMandatory(subtask) {
  if (!subtask) return false;
  if (subtask.mandatory === false) return false;
  if (isWaived(subtask)) return false;
  return !isClosedStatus(subtask.status);
}

const CHILD_STARTED_STATUSES = new Set([
  TaskStatus.IN_PROGRESS,
  TaskStatus.WAITING_ON_EXTERNAL,
  TaskStatus.BLOCKED,
  TaskStatus.UNDER_REVIEW
]);

/** Mirror backend tree.ts: Completed parent reopens when a mandatory child is still open. */
export function derivedRootStatus(rootStatus, subtasks) {
  const rows = Array.isArray(subtasks) ? subtasks : [];
  if (rows.length === 0) return null;
  if (rootStatus === TaskStatus.CANCELLED) return null;
  const open = rows.filter(isOpenMandatory);
  if (open.length === 0) {
    return rootStatus === TaskStatus.COMPLETED ? null : TaskStatus.COMPLETED;
  }
  const anyStarted = rows.some((s) => CHILD_STARTED_STATUSES.has(s.status));
  if (
    rootStatus === TaskStatus.COMPLETED ||
    (anyStarted && rootStatus !== TaskStatus.IN_PROGRESS)
  ) {
    return TaskStatus.IN_PROGRESS;
  }
  return null;
}

export function displayRootStatus(task) {
  if (!task) return '';
  // A court case follows its case status; its actions never complete or reopen
  // it (the backend's syncRootFromChildren skips court cases).
  if (isLegalCaseTask(task)) return task.status;
  const next = derivedRootStatus(task.status, task.subTask || task.subtasks || []);
  return next || task.status;
}

export function trimmedNote(value) {
  return String(value || '').trim();
}

export function subtaskRejectReason(_task, subtask) {
  return trimmedNote(subtask?.rejectReason);
}

export function subtaskStatusLabel(_task, subtask) {
  if (subtask?.status === 'Cancelled' && trimmedNote(subtask?.rejectReason)) {
    return 'Rejected';
  }
  return subtask?.status || '---';
}

export function subtaskInfoTooltip(subtask) {
  const lines = [trimmedNote(subtask?.description) || 'No description'];
  const submit = trimmedNote(subtask?.submitReason);
  const returned = trimmedNote(subtask?.returnReason);
  const rejected = trimmedNote(subtask?.rejectReason);
  if (subtask?.status === 'Under Review') {
    if (returned) lines.push(`Previously returned: ${returned}`);
    if (submit) lines.push(`Submitted: ${submit}`);
  } else if (subtask?.status === 'In Progress' && returned) {
    lines.push(`Returned: ${returned}`);
  } else if (subtask?.status === 'Cancelled' && rejected) {
    lines.push(`Rejected: ${rejected}`);
    if (submit) lines.push(`Submitted: ${submit}`);
  }
  return lines.join('\n');
}
